import { mustFindBy } from "@/mock-api/mustFindBy.ts";
import type { ApiClient } from "@/modules/api/apiClient.ts";
import { getResponseData } from "@/modules/api/apiClient.ts";
import type { ResourceType } from "@/modules/api/resourceTypes.tsx";
import { getLocationsForType, type LocationWithEnabled, type LocationsForType } from "@/modules/location/location.ts";
import { queryThen } from "@/utils/query/mergeQueries.ts";
import { atomFromStandardQueryOptions } from "@/utils/query/queryAtom.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { queryOptions, type QueryKey } from "@tanstack/react-query";
import type { Location } from "@warrenio/api-spec/spec.oats.gen";
import { atom } from "jotai/vanilla";

/** A location identifier, eg. `tll`, `jkt01` */
export type LocationSlug = string;

type Response = Location[];

export const queryKey: QueryKey = ["locations"];

function getQuery(client: ApiClient) {
    return queryOptions({
        queryKey,
        queryFn: async ({ signal }): Promise<Response> =>
            getResponseData(await client.GET("/config/locations", { signal })),
    });
}

export const queryAtom = atomFromStandardQueryOptions(getQuery);

/** NB: Only use this for non-resource purposes.
 * @see {@link useLocationsForType}
 */
function useAllLocationsSuspense() {
    return useSuspenseQueryAtom(queryAtom);
}

/** For viewing pricing, all locations can be selected */
const pricingLocationsAtom = atom((get) =>
    queryThen(get(queryAtom), (locations) => {
        return locations.map((l): LocationWithEnabled => ({ ...l, isEnabled: true }));
    }),
);

export function usePricingLocations() {
    return useSuspenseQueryAtom(pricingLocationsAtom);
}

export function useSingleLocationSuspense(slug: LocationSlug) {
    const data = useAllLocationsSuspense();
    return mustFindBy(data, (l) => l.slug, slug, "Location");
}

export function useLocationsForType(resourceType: ResourceType | undefined): LocationsForType {
    const data = useAllLocationsSuspense();
    return getLocationsForType(data, resourceType);
}
